import { UIPRWithTypedCoverage } from '../../../../../../../api/teamMetricsApi';
import { PRMetrics } from '../types';

/**
 * Get the Monday of the week for a given date string (YYYY-MM-DD)
 */
export function getWeekStart(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay();
  const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  date.setDate(diff);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Format a week start date as "Mon D" (e.g., "Jan 15")
 */
export function formatWeekLabel(weekStart: string): string {
  const [year, month, day] = weekStart.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Get today's date as YYYY-MM-DD string
 */
function getTodayString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * Generate all week start dates between startDate and today
 */
function generateWeekRange(startDate: string): string[] {
  const weeks: string[] = [];
  const startWeek = getWeekStart(startDate);
  const endWeek = getWeekStart(getTodayString());

  let currentWeek = startWeek;
  while (currentWeek <= endWeek) {
    weeks.push(currentWeek);
    const [y, m, d] = currentWeek.split('-').map(Number);
    const nextDate = new Date(y, m - 1, d + 7);
    currentWeek = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`;
  }

  return weeks;
}

export interface WeeklyPRDataPoint {
  week: string;
  improved: number;
  decreased: number;
  neutral: number;
  total: number;
}

export interface PRSummary {
  improved: number;
  decreased: number;
  neutral: number;
  total: number;
}

/**
 * Calculate weekly PR data with coverage impact breakdown.
 * Used for team and repository health dashboards.
 */
export function calculateWeeklyPRData(
  prs: UIPRWithTypedCoverage[],
  startDate: string,
): WeeklyPRDataPoint[] {
  const weeks = generateWeekRange(startDate);
  const weekData: Map<string, { improved: number; decreased: number; neutral: number }> = new Map();

  weeks.forEach((week) => {
    weekData.set(week, { improved: 0, decreased: 0, neutral: 0 });
  });

  prs.forEach((pr) => {
    if (!pr.date) return;
    const weekStart = getWeekStart(pr.date.split('T')[0]);
    if (weekData.has(weekStart)) {
      const data = weekData.get(weekStart)!;
      const combinedDelta = pr.coverageDeltas?.find(c => c.testType === 'go_combined')?.delta;
      if (combinedDelta !== undefined && combinedDelta !== null) {
        if (combinedDelta > 0) {
          data.improved += 1;
        } else if (combinedDelta < 0) {
          data.decreased += 1;
        } else {
          data.neutral += 1;
        }
      } else {
        data.neutral += 1;
      }
    }
  });

  return Array.from(weekData.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, data]) => ({
      week: formatWeekLabel(weekStart),
      improved: data.improved,
      decreased: data.decreased,
      neutral: data.neutral,
      total: data.improved + data.decreased + data.neutral,
    }));
}

/**
 * Calculate PR summary totals with coverage impact breakdown.
 */
export function calculatePRSummary(prs: UIPRWithTypedCoverage[]): PRSummary {
  let improved = 0;
  let decreased = 0;
  let neutral = 0;

  prs.forEach((pr) => {
    const combinedDelta = pr.coverageDeltas?.find(c => c.testType === 'go_combined')?.delta;
    if (combinedDelta !== undefined && combinedDelta !== null) {
      if (combinedDelta > 0) {
        improved += 1;
      } else if (combinedDelta < 0) {
        decreased += 1;
      } else {
        neutral += 1;
      }
    } else {
      neutral += 1;
    }
  });

  return { improved, decreased, neutral, total: improved + decreased + neutral };
}

export interface SimpleWeeklyPRDataPoint {
  week: string;
  prCount: number;
}

/**
 * Calculate simple weekly PR counts (no coverage breakdown).
 * Used for user health dashboard.
 */
export function calculateSimpleWeeklyPRData(
  prs: PRMetrics[],
  startDate: string,
): SimpleWeeklyPRDataPoint[] {
  const weeks = generateWeekRange(startDate);
  const weekCounts: Map<string, number> = new Map();

  weeks.forEach((week) => {
    weekCounts.set(week, 0);
  });

  prs.forEach((pr) => {
    const weekStart = getWeekStart(pr.date);
    if (weekCounts.has(weekStart)) {
      weekCounts.set(weekStart, (weekCounts.get(weekStart) || 0) + 1);
    }
  });

  return Array.from(weekCounts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekStart, count]) => ({
      week: formatWeekLabel(weekStart),
      prCount: count,
    }));
}
